/**
 * Which UI an area draws: the classic one or the redesign — a release STAGE per area, plus one
 * "classic Teamio" / "new Teamio" choice per office employee (stages: 2026-10-10; the follow-up page
 * gate itself: Phase 0, 2026-10-02).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Nilay's decision (2026-10-02): the redesign is built NEXT TO the old pages. Each area in PAGE_KEYS
 * sits at one STAGE (the STAGE map below); moving an area is a one-line change in a normal commit,
 * plus its one pin in cypress/node-tests/followups-ui-version.test.js. There is no site setting (so no
 * seeder work).
 *
 *   stage     not a Super Admin                       Super Admin
 *   preview   the old page, always                   the new page, unless their own switch says 'old'
 *   invite    the old page, unless they chose 'new'   unchanged
 *   default   the new page, unless they chose 'old'   unchanged
 *   retired   the new page, always                    the new page, always
 *
 *   • Every area ships at 'preview', which is how the app behaved before stages existed.
 *   • An unknown page key, or a stage value that is not in STAGES, ⇒ the old page for everyone (a typo
 *     must never ship the new UI).
 *   • A Super Admin (isSuperAdmin === true, exactly — Teamio staff only) keeps the separate switches
 *     described below, exactly as before, at every stage but 'retired'. A Super Admin NEVER reads the
 *     office choice, and an office user NEVER reads a Super Admin key.
 *
 * ── THE OFFICE CHOICE (2026-10-10) ──────────────────────────────────────────────────────────────
 * One localStorage key per person, `teamio_ui_choice:<employeeID>` = 'old' / 'new' (unset ⇒ follow the
 * stage), covers every area at once. It lives in the browser, keyed by employee, so two people on one
 * computer keep separate choices. `userKey` is that employee ID: String(...).trim(); null / undefined /
 * '' means "no person" ⇒ no choice is read (the stage's default) and none can be written. It only
 * matters at 'invite' and 'default'. officeSwitchState() says whether the "Switch to classic Teamio" /
 * "Try the new Teamio" control shows and which way a click goes. CLASSIC_UNTIL is the date shown to
 * users; nothing switches off by itself on that date.
 *
 * localStorage can throw (private window, blocked site data), so every access is wrapped; a throw
 * reads as "no override" / "no choice".
 *
 * ── THE DRAWER HAS ITS OWN OVERRIDE (drawer plan Phase 6c, 2026-10-02) ──────────────────────────
 * A Super Admin who picks "Classic drawer" in the new drawer's footer wants the OLD DRAWER, not the
 * old pages. So 'drawer' reads its own key, `teamio_followups_drawer_ui` ('old' / 'new'), and only
 * when that is unset falls back to the shared page key `teamio_followups_ui` (so "Classic view" on a
 * page still means "everything classic", as it did before 6c). setDrawerUiChoice writes only the
 * drawer key; the page "New view" link (setFollowupsUiChoice 'new') clears BOTH — that is the way back
 * to the new drawer from the classic one (CreateFollowup.vue is frozen, so it cannot carry a link of
 * its own). Every other page key reads the shared page key.
 *
 * ── THE APP SHELL HAS ITS OWN KEY, WITH NO FALLBACK (app shell plan, 2026-10-08) ─────────────────
 * 'nav' is the new sidebar + top bar (src/components/AppShell/, rules in src/utils/nav/), not a
 * page. A Super Admin reads ONLY `teamio_nav_ui` ('old' / 'new') for it — never the shared page
 * key — so Nilay can run the classic pages under the new shell, or the new pages under the classic
 * shell, and compare either one alone. Unset ⇒ the new shell (the Super Admin default, as for the
 * pages). setNavUiChoice writes only the nav key; "Classic view" / "New view" on a page
 * (setFollowupsUiChoice) never touch it. Everyone else follows STAGE.nav and the office choice.
 *
 * ── THE PATIENT PROFILE LAYOUT HAS ITS OWN KEY, WITH NO FALLBACK (profile overhaul, 2026-10-08) ──
 * 'patientProfileLayout' is the whole Patient Profile page rebuilt on the Schedule View patient
 * panel's components (design 1a — docs/plans/2026-10-08-patient-profile-overhaul-plan.md). It is NOT
 * the 'patientProfile' key, which only picks the Follow-ups SECTION inside the classic page. A Super
 * Admin reads ONLY `teamio_patient_profile_ui` ('old' / 'new'); unset ⇒ the new page.
 * setProfileLayoutUiChoice writes only that key. Everyone else follows STAGE.patientProfileLayout and
 * the office choice.
 *
 * ── WORKFLOW HAS ITS OWN KEY, WITH NO FALLBACK (new-UI release plan, 2026-10-10) ─────────────────
 * 'workflow' is the Workflow overhaul (built in its own session). A Super Admin reads ONLY
 * `teamio_workflow_ui` ('old' / 'new'); unset ⇒ the new Workflow. Everyone else follows STAGE.workflow
 * and the office choice. setAreaUiChoice('workflow', …) writes only that key.
 *
 * ── REMINDER MESSAGES HAVE THEIR OWN KEY, WITH NO FALLBACK (inline reminder messages, 2026-10-17) ──
 * 'reminderMessages' is Appointment Reminders → Settings drawing the 11 SMS "Template for …" slots as plain
 * message boxes (src/components/MessageTemplateBox.vue) instead of wiki-template dropdowns. A Super Admin
 * reads ONLY `teamio_reminder_messages_ui` ('old' / 'new'); unset ⇒ the boxes. Everyone else follows
 * STAGE.reminderMessages and the office choice. setAreaUiChoice('reminderMessages', …) writes only that
 * key. Released to every office on 2026-10-18 (Nilay, after a prod census showed no office would start sending
 * anything new): it ships at 'retired' — the boxes for everyone, the classic / new link hidden for everyone
 * (FollowupsUiToggle), and the stored key / office choice no longer read. A wiki template an office already picked
 * stays a dropdown with its own "Switch to a plain message" link.
 *
 * ── FORMS SETTINGS HAS ITS OWN KEY, WITH NO FALLBACK (Forms Settings, 2026-10-20) ────────────────
 * 'formsSettings' is the Forms page's one full-screen "Forms Settings" editor (New Patients · Medical History
 * Update · Procedure mappings · Post-op message; docs/plans/2026-10-20-forms-settings-plan.md D19) in place of the
 * settings cog's Medical History modal + the 🔗 two-tab Procedure Forms editor. A Super Admin reads ONLY
 * `teamio_forms_settings_ui` ('old' / 'new'); unset ⇒ the new editor. Everyone else follows STAGE.formsSettings and
 * the office choice. setAreaUiChoice('formsSettings', …) writes only that key. It ships at 'preview' (Super Admins
 * only), so every office sees today's screens until Nilay moves the stage.
 *
 * nav, patientProfileLayout, workflow, reminderMessages and formsSettings share one small table (OWN_KEY_AREAS), and
 * setAreaUiChoice is the one Super Admin setter for any area — so a new own-key area is one table row,
 * not another read / write pair.
 */

export const STORAGE_KEY = 'teamio_followups_ui';
export const DRAWER_STORAGE_KEY = 'teamio_followups_drawer_ui';
export const NAV_STORAGE_KEY = 'teamio_nav_ui';
export const PROFILE_LAYOUT_STORAGE_KEY = 'teamio_patient_profile_ui';
export const WORKFLOW_STORAGE_KEY = 'teamio_workflow_ui';
export const REMINDER_MESSAGES_STORAGE_KEY = 'teamio_reminder_messages_ui';
export const FORMS_SETTINGS_STORAGE_KEY = 'teamio_forms_settings_ui';

/** The office choice is stored per person: OFFICE_CHOICE_PREFIX + employee ID. */
export const OFFICE_CHOICE_PREFIX = 'teamio_ui_choice:';

/** Display only — the last day "classic Teamio" is offered. Nothing switches off by itself. */
export const CLASSIC_UNTIL = '2026-11-09';

export const PAGE_KEYS = [
  'overdueRecall', 'overdueRecallList', 'unscheduledTreatment', 'unscheduledList',
  'aging', 'brokenAppointments', 'patientProfile', 'admin',
  // The follow-up drawer (Phase 6, docs/plans/2026-10-02-followups-drawer-plan.md). Read by
  // followupActions.openDrawer only (6c), with its own Super Admin override (DRAWER_STORAGE_KEY).
  'drawer',
  // The Tasks page redesign (design "Tasks 2a", Phase 7B — /app/todo, src/pages/TaskList/ToDo.vue).
  'tasks',
  // The app shell — sidebar 1b + top bar 1d (src/components/AppShell/). Own key, no fallback.
  'nav',
  // The Patient Profile page overhaul (design 1a, built on src/components/PatientPanel/). Own key, no fallback.
  'patientProfileLayout',
  // The Workflow overhaul (built in its own session). Own key (WORKFLOW_STORAGE_KEY), no fallback.
  'workflow',
  // Appointment Reminders → Settings: plain message boxes for the SMS slots. Own key (REMINDER_MESSAGES_STORAGE_KEY), no fallback.
  'reminderMessages',
  // Forms → Settings: the one full-screen "Forms Settings" editor. Own key (FORMS_SETTINGS_STORAGE_KEY), no fallback.
  'formsSettings',
];

/** The release stages, in order. */
export const STAGES = ['preview', 'invite', 'default', 'retired'];

/**
 * The stage of each area. Move an area by changing its line here (a normal commit, Nilay's go) and its
 * pin in cypress/node-tests/followups-ui-version.test.js — nothing else may break when a stage moves.
 * A plain object on purpose (component specs set a stage in a test and restore it).
 */
export const STAGE = {
  overdueRecall: 'default',
  overdueRecallList: 'default',
  unscheduledTreatment: 'default',
  unscheduledList: 'default',
  aging: 'default',
  brokenAppointments: 'default',
  patientProfile: 'default',
  admin: 'default',
  drawer: 'default',
  // Tasks page redesign (design "Tasks 2a", Phase 7B).
  tasks: 'default',
  // App shell (sidebar + top bar).
  nav: 'default',
  // Patient Profile overhaul (the whole page).
  patientProfileLayout: 'default',
  // Workflow overhaul.
  workflow: 'default',
  // Inline reminder messages (Appointment Reminders → Settings, SMS slots). Released to every office 2026-10-18 (Nilay).
  reminderMessages: 'retired',
  // Forms Settings (Forms page: one Settings button → New Patients / Medical History / mappings / post-op). Super Admins only for now.
  formsSettings: 'preview',
};

/** The areas with their own Super Admin key and NO fallback to the shared page key. */
const OWN_KEY_AREAS = {
  nav: NAV_STORAGE_KEY,
  patientProfileLayout: PROFILE_LAYOUT_STORAGE_KEY,
  workflow: WORKFLOW_STORAGE_KEY,
  reminderMessages: REMINDER_MESSAGES_STORAGE_KEY,
  formsSettings: FORMS_SETTINGS_STORAGE_KEY,
};

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/** One localStorage key's 'old' / 'new', or null (unset / unreadable / anything else). */
function readChoiceKey(storage, key) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return null;
    const v = storage.getItem(key);
    return v === 'old' || v === 'new' ? v : null;
  } catch (e) {
    return null;
  }
}

/** Sets ('old' / 'new') or clears (anything else) one localStorage key. False if storage refused. */
function writeChoiceKey(storage, key, value) {
  try {
    if (!storage) return false;
    if (value === 'old' || value === 'new') storage.setItem(key, value);
    else storage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}

/** 'old' stores, 'new' CLEARS (the new UI is the Super Admin default), anything else is refused. */
function setChoiceKey(storage, key, choice) {
  if (choice === 'old') return writeChoiceKey(storage, key, 'old');
  if (choice === 'new') return writeChoiceKey(storage, key, null);
  return false;
}

/** The Super Admin override: 'old', 'new' or null (unset / unreadable). */
export function readOverride(storage) {
  return readChoiceKey(storage, STORAGE_KEY);
}

/** Sets ('old' / 'new') or clears (anything else) the override. Returns false if storage refused. */
export function writeOverride(storage, value) {
  return writeChoiceKey(storage, STORAGE_KEY, value);
}

/**
 * The browser's localStorage, or null. Reading `window.localStorage` itself can throw (blocked
 * site data), so a page passes this — never the bare global — to the functions here.
 */
export function browserStorage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

/**
 * The Super Admin "Classic view" / "New view" links. 'old' stores the override; 'new' CLEARS it
 * (the new page is already the Super Admin default, so nothing is left behind in storage).
 * Anything else is refused. Returns false when the choice was refused or storage would not take it.
 */
export function setFollowupsUiChoice(storage, choice) {
  if (choice === 'old') return writeOverride(storage, 'old');
  if (choice === 'new') {
    const pages = writeOverride(storage, null);
    const drawer = writeDrawerOverride(storage, null);
    return pages && drawer;
  }
  return false;
}

/** The Super Admin DRAWER override: 'old', 'new' or null (unset / unreadable). */
export function readDrawerOverride(storage) {
  return readChoiceKey(storage, DRAWER_STORAGE_KEY);
}

/** Sets ('old' / 'new') or clears (anything else) the drawer override. False if storage refused. */
export function writeDrawerOverride(storage, value) {
  return writeChoiceKey(storage, DRAWER_STORAGE_KEY, value);
}

/**
 * The new drawer's "Classic drawer" link ('old') — and, for completeness, 'new' (clears the drawer
 * key). Never touches the page key. Anything else is refused.
 */
export function setDrawerUiChoice(storage, choice) {
  return setChoiceKey(storage, DRAWER_STORAGE_KEY, choice);
}

/** The Super Admin APP SHELL override: 'old', 'new' or null (unset / unreadable). */
export function readNavOverride(storage) {
  return readChoiceKey(storage, OWN_KEY_AREAS.nav);
}

/** Sets ('old' / 'new') or clears (anything else) the app shell override. False if storage refused. */
export function writeNavOverride(storage, value) {
  return writeChoiceKey(storage, OWN_KEY_AREAS.nav, value);
}

/**
 * The shell's "Classic navigation" link ('old') and the header gear's "New navigation" ('new',
 * which clears the key — the new shell is already the Super Admin default). Never touches the
 * page or drawer keys. Anything else is refused.
 */
export function setNavUiChoice(storage, choice) {
  return setChoiceKey(storage, OWN_KEY_AREAS.nav, choice);
}

/** The Super Admin PATIENT PROFILE LAYOUT override: 'old', 'new' or null (unset / unreadable). */
export function readProfileLayoutOverride(storage) {
  return readChoiceKey(storage, OWN_KEY_AREAS.patientProfileLayout);
}

/** Sets ('old' / 'new') or clears (anything else) the profile layout override. False if storage refused. */
export function writeProfileLayoutOverride(storage, value) {
  return writeChoiceKey(storage, OWN_KEY_AREAS.patientProfileLayout, value);
}

/**
 * The new profile's "Classic profile" link ('old') and the classic page's "New profile" link ('new',
 * which clears the key — the new page is already the Super Admin default). Never touches the page,
 * drawer or nav keys. Anything else is refused.
 */
export function setProfileLayoutUiChoice(storage, choice) {
  return setChoiceKey(storage, OWN_KEY_AREAS.patientProfileLayout, choice);
}

/**
 * The Super Admin setter for any area. For nav / patientProfileLayout / workflow / reminderMessages / formsSettings it writes
 * that area's own key only; for 'drawer' it is setDrawerUiChoice; for every other known key it is
 * setFollowupsUiChoice. 'old' stores, 'new' clears, anything else is refused. An unknown key ⇒ false.
 */
export function setAreaUiChoice(pageKey, storage, choice) {
  if (PAGE_KEYS.indexOf(pageKey) === -1) return false;
  if (hasOwn(OWN_KEY_AREAS, pageKey)) return setChoiceKey(storage, OWN_KEY_AREAS[pageKey], choice);
  if (pageKey === 'drawer') return setDrawerUiChoice(storage, choice);
  return setFollowupsUiChoice(storage, choice);
}

/** An employee ID as a storage-key suffix, or null for "no person" (null / undefined / blank). */
function officeKeyOf(userKey) {
  if (userKey === null || userKey === undefined) return null;
  const k = String(userKey).trim();
  return k === '' ? null : k;
}

/** This person's office choice: 'old', 'new' or null (no person / unset / unreadable). */
export function readOfficeChoice(storage, userKey) {
  const k = officeKeyOf(userKey);
  if (k === null) return null;
  return readChoiceKey(storage, OFFICE_CHOICE_PREFIX + k);
}

/**
 * Stores ('old' or 'new' — both are kept) or clears (anything else) this person's office choice.
 * False when there is no person or storage refused.
 */
export function setOfficeChoice(storage, userKey, choice) {
  const k = officeKeyOf(userKey);
  if (k === null) return false;
  return writeChoiceKey(storage, OFFICE_CHOICE_PREFIX + k, choice);
}

/** The stage of an area, or null for an unknown key or a value that is not in STAGES. */
export function stageOf(pageKey, stages = STAGE) {
  if (!stages || PAGE_KEYS.indexOf(pageKey) === -1 || !hasOwn(stages, pageKey)) return null;
  const stage = stages[pageKey];
  return STAGES.indexOf(stage) === -1 ? null : stage;
}

/** What a Super Admin sees (today's rules): their own switch, or the new UI when it is unset. */
function superAdminUsesNew(pageKey, storage) {
  if (hasOwn(OWN_KEY_AREAS, pageKey)) return readChoiceKey(storage, OWN_KEY_AREAS[pageKey]) !== 'old';
  if (pageKey === 'drawer') {
    const drawer = readDrawerOverride(storage);
    if (drawer !== null) return drawer !== 'old';
  }
  return readOverride(storage) !== 'old';
}

export function shouldUseNewFollowupsUi(pageKey, { isSuperAdmin, storage, userKey, stages } = {}) {
  const stage = stageOf(pageKey, stages);
  if (stage === null) return false;
  if (stage === 'retired') return true;
  if (isSuperAdmin === true) return superAdminUsesNew(pageKey, storage);
  if (stage === 'preview') return false;
  const choice = readOfficeChoice(storage, userKey);
  if (stage === 'invite') return choice === 'new';
  return choice !== 'old'; // 'default'
}

/**
 * The office "Switch to classic Teamio" / "Try the new Teamio" control: { visible, target }.
 * Hidden for a Super Admin, in a demo session (`isDemo` true: the demo shows the new Teamio only —
 * Saumil 8a, 2026-10-21), when there is no person, and when no area is at 'invite' / 'default'.
 * Otherwise visible, and `target` is 'old' when ANY of those areas currently draws the new UI for this
 * person, else 'new'.
 */
export function officeSwitchState({ isSuperAdmin, storage, userKey, stages, isDemo } = {}) {
  const hidden = { visible: false, target: null };
  if (isSuperAdmin === true || isDemo === true) return hidden;
  if (officeKeyOf(userKey) === null) return hidden;
  const switchable = PAGE_KEYS.filter((k) => {
    const stage = stageOf(k, stages);
    return stage === 'invite' || stage === 'default';
  });
  if (switchable.length === 0) return hidden;
  const onNew = switchable.some(k => shouldUseNewFollowupsUi(k, { isSuperAdmin: false, storage, userKey, stages }));
  return { visible: true, target: onNew ? 'old' : 'new' };
}
